import { tr as __tr, useLocale as __useLocale, direction as __direction, localizedField as __localizedField } from "../../i18n/runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import CurrencySelect from '../../components/CurrencySelect';
import { useAuth } from '../../context/AuthContext';
const currencies = ['USD', 'YER', 'SAR', 'AED', 'EUR', 'GBP'];
const typeNames = {
  asset: "أصول",
  liability: "التزامات",
  equity: "حقوق ملكية",
  revenue: "إيرادات",
  expense: "مصروفات"
};
const statusNames = {
  draft: "مسودة",
  sent: "معتمدة",
  paid: "مسددة",
  cancelled: "ملغاة",
  overdue: "متأخرة"
};
const today = () => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Aden',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
}).format(new Date());
const money = value => value == null ? "غير محدد" : Number(value).toLocaleString(__direction() === 'rtl' ? 'ar' : 'en', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const fieldClass = 'block w-full border rounded-lg p-2 mt-1 bg-white';
const buttonClass = 'rounded-lg px-4 py-2 bg-[#6B5B95] text-white disabled:opacity-50';
const keyForRequest = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};
function Field({
  label,
  children
}) {
  __useLocale();
  return <label className="block text-sm text-gray-700">{__tr(label)}{__tr(children)}</label>;
}
function Currency({
  value,
  onChange
}) {
  __useLocale();
  return <Field label={__tr("العملة")}><CurrencySelect required className={fieldClass} value={value} onChange={e => onChange(e.target.value)} /></Field>;
}
function AccountSelect({
  label,
  accounts,
  currency,
  type,
  value,
  onChange,
  required = true,
  exclude
}) {
  __useLocale();
  const choices = accounts.filter(a => a.currency === currency && Number(a.is_active) && a.is_leaf && (!type || a.account_type === type) && a.id !== exclude);
  return <Field label={__tr(label)}><select className={fieldClass} required={required} value={value || ''} onChange={e => onChange(e.target.value)}><option value="">{__tr("اختر الحساب")}</option>{__tr(choices.map(a => <option key={a.id} value={a.id}>{__tr(a.code)} — {__tr(__localizedField(a, "name_ar"))}</option>))}</select></Field>;
}
function DataTable({
  headings,
  rows
}) {
  __useLocale();
  return <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr>{__tr(headings.map(h => <th className="p-3 border-b text-right whitespace-nowrap" key={h}>{__tr(h)}</th>))}</tr></thead><tbody>{__tr(rows.map((cells, index) => <tr key={index}>{__tr(cells.map((cell, j) => <td className="p-3 border-b align-top" key={j}>{__tr(cell)}</td>))}</tr>))}</tbody></table>{__tr(!rows.length && <p className="py-6 text-gray-500">{__tr("لا توجد بيانات.")}</p>)}</div>;
}
export default function FinancePage({
  view
}) {
  __useLocale();
  const {
    token,
    isAdmin
  } = useAuth();
  const [data, setData] = useState(null);
  const [lookups, setLookups] = useState({
    accounts: [],
    customers: [],
    bookings: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [reversal, setReversal] = useState(null);
  const running = useRef(false);
  const pending = useRef(null);
  const mounted = useRef(true);
  const generation = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };
      const [result, options] = await Promise.all([axios.get('/api/finance.php', {
        params: {
          view
        },
        headers
      }), axios.get('/api/finance.php?view=lookups', {
        headers
      })]);
      if (mounted.current && current === generation.current) {
        setData(result.data);
        setLookups(options.data);
      }
    } catch (e) {
      if (mounted.current && current === generation.current) setError(e.response?.data?.message || "تعذر تحميل المحاسبة");
    } finally {
      if (mounted.current && current === generation.current) setLoading(false);
    }
  }, [view, token]);
  useEffect(() => {
    setData(null);
    setMessage('');
    load();
  }, [load]);
  const submit = async (action, payload) => {
    if (running.current) return false;
    running.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    const signature = JSON.stringify([action, payload]);
    try {
      if (pending.current?.signature !== signature) pending.current = {
        signature,
        key: keyForRequest()
      };
      await axios.post(`/api/finance.php?action=${action}`, {
        ...payload,
        request_key: pending.current.key
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      pending.current = null;
      setMessage(__tr("تم حفظ العملية بنجاح"));
      await load();
      return true;
    } catch (e) {
      setError(e.response?.data?.message || "تعذر تأكيد الحفظ. يمكنك إعادة المحاولة بنفس البيانات دون تكرار العملية.");
      return false;
    } finally {
      running.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const inspect = async (kind, id) => {
    try {
      const res = await axios.get('/api/finance.php', {
        params: {
          view: kind,
          id
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return res.data;
    } catch (e) {
      setError(e.response?.data?.message || "تعذر تحميل التفاصيل");
      return null;
    }
  };
  const title = {
    receipts: "سندات القبض",
    'payment-vouchers': "سندات الصرف",
    accounts: "الحسابات والأرصدة",
    invoices: "الفواتير والتحصيل",
    journal: "القيود اليومية",
    trial: "ميزان المراجعة"
  }[view];
  return <section className="bg-white p-5 rounded-xl shadow space-y-5" dir={__direction()}>
    <div className="flex justify-between gap-4"><h2 className="font-bold text-2xl">{__tr(view === 'checkout-payments' ? "مشتريات العملاء وتسويتها" : title)}</h2><button disabled={busy || loading} onClick={load} className="border rounded px-4">{__tr("تحديث")}</button></div>
    {__tr(error && <p role="alert" className="bg-red-50 text-red-700 p-3 rounded whitespace-pre-wrap">{__tr(error)}</p>)}
    {__tr(message && <p role="status" className="text-green-700">{__tr(message)}</p>)}
    {__tr(loading && <p role="status">{__tr("جارٍ التحميل…")}</p>)}
    {__tr(data && <fieldset disabled={busy || loading} className="min-w-0 space-y-4">
      {__tr(reversal && <ReversalForm operation={reversal} onCancel={() => setReversal(null)} onSave={async payload => {
        if (await submit(reversal.action, payload)) setReversal(null);
      }} />)}
      {__tr(view === 'accounts' && <AccountsPanel accounts={data} submit={submit} inspect={inspect} />)}
      {__tr(view === 'invoices' && <InvoicesPanel invoices={data} lookups={lookups} submit={submit} inspect={inspect} isAdmin={isAdmin} onReverse={setReversal} />)}
      {__tr(view === 'journal' && <JournalPanel entries={data} accounts={lookups.accounts} submit={submit} onReverse={setReversal} />)}
      {__tr(view === 'trial' && <TrialPanel data={data} />)}
      {__tr(view === 'checkout-payments' && <CheckoutPayments rows={data} accounts={lookups.accounts} submit={submit} />)}
      {__tr(['receipts', 'payment-vouchers'].includes(view) && <VouchersPanel rows={data} view={view} lookups={lookups} submit={submit} onReverse={setReversal} />)}
    </fieldset>)}
  </section>;
}
function CheckoutPayments({
  rows,
  accounts,
  submit
}) {
  __useLocale();
  const [form, setForm] = useState(null);
  return <><p>{__tr("اعتماد التسوية ينشئ فاتورة معتمدة وسند قبض، ويستخدم حركة الدفع الموجودة دون تكرارها. للدفعات اليدوية تأكد من استلام المبلغ أولًا. قيمة الشراء الحالية دون ضريبة إضافية.")}</p>
 {__tr(form && <form className="border rounded p-4 space-y-3" onSubmit={async e => {
      e.preventDefault();
      if (window.confirm("" + __tr("تأكيد استلام وترحيل ") + form.amount + " " + form.currency + __tr("؟")) && (await submit('checkout-settle', form))) setForm(null);
    }}><h3>{__tr(form.customer_name)} — {__tr(form.amount)} {__tr(form.currency)}</h3><AccountSelect label={__tr("ذمم العميل")} type="asset" accounts={accounts} currency={form.currency} value={form.receivable_account_id} onChange={v => setForm({
        ...form,
        receivable_account_id: v
      })} /><AccountSelect label={__tr("الإيراد")} type="revenue" accounts={accounts} currency={form.currency} value={form.revenue_account_id} onChange={v => setForm({
        ...form,
        revenue_account_id: v
      })} /><AccountSelect label={__tr("الصندوق أو حساب PayPal / البنك")} type="asset" accounts={accounts} currency={form.currency} exclude={form.receivable_account_id} value={form.cash_account_id} onChange={v => setForm({
        ...form,
        cash_account_id: v
      })} /><Field label={__tr("تاريخ التحصيل")}><input required className={fieldClass} type="date" value={form.date} onChange={e => setForm({
          ...form,
          date: e.target.value
        })} /></Field><button className={buttonClass}>{__tr("اعتماد التسوية")}</button> <button type="button" onClick={() => setForm(null)}>{__tr("إلغاء")}</button></form>)}
 <DataTable headings={["الطلب", "العميل", "المبلغ", "العملة", "الدفع", "الحالة", "المحاسبة"]} rows={rows.map(r => [r.id.slice(0, 8), r.customer_name, money(r.amount), r.currency, r.payment_method, {
      pending: "بانتظار الدفع",
      paid: "مسدد",
      test_paid: "تجريبي",
      cancelled: "ملغى"
    }[r.status], r.invoice_id ? "مرحّل إلى الفواتير" : ['pending', 'paid'].includes(r.status) && (r.payment_method !== 'paypal' || r.status === 'paid') ? <button className="text-purple-700" onClick={() => setForm({
      ...r,
      date: today(),
      receivable_account_id: '',
      revenue_account_id: '',
      cash_account_id: ''
    })}>{__tr("تسوية محاسبية")}</button> : "لا يمكن الترحيل"])} /></>;
}
function VouchersPanel({
  rows,
  view,
  lookups,
  submit,
  onReverse
}) {
  __useLocale();
  const [form, setForm] = useState(null);
  const receipt = view === 'receipts';
  const change = (key, value) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  return <>
    <p className="text-sm text-gray-600">{__tr("السند المستقل يُرحّل مباشرة إلى الحسابات. لتحصيل فاتورة وتسوية المتبقي عليها استخدم «تحصيل» من قائمة الفواتير.")}</p>
    <button className={buttonClass} onClick={() => setForm({
      voucher_type: receipt ? 'receipt' : 'payment',
      currency: '',
      amount: '',
      date: today(),
      description: '',
      cash_account_id: '',
      account_id: '',
      user_id: '',
      booking_id: ''
    })}>{__tr("إضافة سند ")}{__tr(receipt ? "قبض" : "صرف")}</button>
    {__tr(form && <form className="border rounded p-4 space-y-4" onSubmit={async e => {
      e.preventDefault();
      if (window.confirm(__tr("حفظ السند وترحيله إلى الأرصدة؟")) && (await submit('voucher-create', form))) setForm(null);
    }}>
      <div className="grid md:grid-cols-3 gap-4">
        <Currency value={form.currency} onChange={currency => setForm({
          ...form,
          currency,
          cash_account_id: '',
          account_id: '',
          booking_id: ''
        })} />
        <Field label={__tr("المبلغ")}><input required type="number" min="0.01" step="0.01" className={fieldClass} value={form.amount} onChange={e => change('amount', e.target.value)} /></Field>
        <Field label={__tr("التاريخ")}><input required type="date" className={fieldClass} value={form.date} onChange={e => change('date', e.target.value)} /></Field>
        <AccountSelect label={__tr("الصندوق أو البنك")} accounts={lookups.accounts} currency={form.currency} type="asset" value={form.cash_account_id} onChange={v => change('cash_account_id', v)} />
        <AccountSelect label={__tr("الحساب المقابل")} accounts={lookups.accounts} currency={form.currency} exclude={form.cash_account_id} value={form.account_id} onChange={v => change('account_id', v)} />
        <Field label={__tr("العميل (اختياري)")}><select className={fieldClass} value={form.user_id} onChange={e => setForm({
            ...form,
            user_id: e.target.value,
            booking_id: ''
          })}><option value="">{__tr("دون عميل")}</option>{__tr(lookups.customers.map(c => <option key={c.id} value={c.id}>{__tr(c.full_name)}</option>))}</select></Field>
        <Field label={__tr("العملية (اختياري)")}><select className={fieldClass} value={form.booking_id} onChange={e => change('booking_id', e.target.value)}><option value="">{__tr("دون عملية")}</option>{__tr(lookups.bookings.filter(b => String(b.user_id) === String(form.user_id) && b.currency === form.currency).map(b => <option key={b.id} value={b.id}>{__tr(b.booking_type)} — {__tr(b.id)}</option>))}</select></Field>
        <Field label={__tr("البيان")}><input required maxLength={2000} className={fieldClass} value={form.description} onChange={e => change('description', e.target.value)} /></Field>
      </div><p className="text-sm">{__tr(receipt ? "يزيد رصيد الصندوق أو البنك ويُسجل الحساب المقابل دائنًا." : "يخفض رصيد الصندوق أو البنك ويُسجل الحساب المقابل مدينًا.")}</p><button className={buttonClass}>{__tr("حفظ وترحيل")}</button> <button type="button" onClick={() => setForm(null)}>{__tr("إلغاء")}</button>
    </form>)}
    <DataTable headings={["رقم السند", "التاريخ", "المبلغ", "العملة", "الحساب", "الصندوق / البنك", "البيان", "الحالة", "الإجراء"]} rows={rows.map(v => [v.voucher_number, v.voucher_date, money(v.amount), v.currency, v.account_name, v.cash_name, v.description, v.reversal_entry_id ? "ملغى بقيد عكسي" : Number(v.is_posted) ? "مرحّل" : "مسودة", v.source_key?.startsWith('voucher:') && !v.reversal_entry_id ? <button className="text-red-700" onClick={() => onReverse({
      id: v.id,
      action: 'voucher-reverse',
      label: "إلغاء السند"
    })}>{__tr("إلغاء وإعادة الرصيد")}</button> : null])} />
  </>;
}
function ReversalForm({
  operation,
  onCancel,
  onSave
}) {
  __useLocale();
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(today());
  return <form className="border border-red-200 bg-red-50 rounded p-4 space-y-3" onSubmit={async e => {
    e.preventDefault();
    if (window.confirm("" + __tr("تأكيد ") + operation.label + __tr("؟ سيُسجل قيد عكسي مع الاحتفاظ بالأصل."))) await onSave({
      id: operation.id,
      reason,
      date
    });
  }}><h3 className="font-bold">{__tr(operation.label)}</h3><p className="text-sm">{__tr("هذا تصحيح محاسبي للعملية كاملة؛ لا ينفذ تحويلًا أو استردادًا لدى البنك. عكس التحصيل يعيد المديونية على الفاتورة، ولا يلغي الفاتورة نفسها.")}</p>
    <Field label={__tr("سبب الإلغاء")}><input required maxLength={1000} className={fieldClass} value={reason} onChange={e => setReason(e.target.value)} /></Field>
    <Field label={__tr("تاريخ القيد العكسي")}><input required type="date" className={fieldClass} value={date} onChange={e => setDate(e.target.value)} /></Field>
    <button className={buttonClass}>{__tr("تأكيد الإلغاء وإعادة الرصيد")}</button> <button type="button" onClick={onCancel}>{__tr("تراجع")}</button>
  </form>;
}
function AccountsPanel({
  accounts,
  submit,
  inspect
}) {
  __useLocale();
  const [form, setForm] = useState(null);
  const [statement, setStatement] = useState(null);
  const [currencyId, setCurrencyId] = useState('');
  const [currency, setCurrency] = useState('USD');
  return <>
    <p className="text-sm text-gray-600">{__tr("الأرصدة محسوبة من القيود المرحلة. حسابات الأصول والمصروفات طبيعتها مدينة، وبقية الأنواع دائنة. رصيد المجموعة يشمل فروعها ولا يُجمع مرة ثانية في ميزان المراجعة.")}</p>
    <button className={buttonClass} onClick={() => setForm({
      code: '',
      name_ar: '',
      name_en: '',
      account_type: 'asset',
      currency: '',
      parent_id: ''
    })}>{__tr("إضافة حساب")}</button>
    {__tr(form && <form className="border rounded p-4 space-y-4" onSubmit={async e => {
      e.preventDefault();
      if (await submit('account-create', form)) setForm(null);
    }}>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label={__tr("رمز الحساب")}><input required maxLength={20} className={fieldClass} value={form.code} onChange={e => setForm({
            ...form,
            code: e.target.value
          })} /></Field>
        <Field label={__tr("اسم الحساب")}><input required className={fieldClass} value={form.name_ar} onChange={e => setForm({
            ...form,
            name_ar: e.target.value
          })} /></Field>
        <Field label={__tr("النوع")}><select className={fieldClass} value={form.account_type} onChange={e => setForm({
            ...form,
            account_type: e.target.value,
            parent_id: ''
          })}>{__tr(Object.entries(typeNames).map(([v, label]) => <option key={v} value={v}>{__tr(label)}</option>))}</select></Field>
        <Currency value={form.currency} onChange={currency => setForm({
          ...form,
          currency,
          parent_id: ''
        })} />
        <Field label={__tr("الحساب الأب (اختياري)")}><select className={fieldClass} value={form.parent_id} onChange={e => setForm({
            ...form,
            parent_id: e.target.value
          })}><option value="">{__tr("حساب مستقل")}</option>{__tr(accounts.filter(a => a.currency === form.currency && a.account_type === form.account_type && Number(a.is_active)).map(a => <option key={a.id} value={a.id}>{__tr(a.code)} — {__tr(__localizedField(a, "name_ar"))}</option>))}</select></Field>
      </div><button className={buttonClass}>{__tr("حفظ")}</button> <button type="button" onClick={() => setForm(null)}>{__tr("إلغاء")}</button>
      <p className="text-sm text-gray-500">{__tr("الرصيد الافتتاحي يُسجل بقيد متوازن مقابل حساب افتتاحي مناسب، ولا يُكتب مباشرة في الحساب.")}</p>
    </form>)}
    {__tr(currencyId && <form className="border p-4 rounded flex items-end gap-3" onSubmit={async e => {
      e.preventDefault();
      if (await submit('account-currency', {
        id: currencyId,
        currency
      })) setCurrencyId('');
    }}><Currency value={currency} onChange={setCurrency} /><button className={buttonClass}>{__tr("تحديد العملة للحساب")}</button><button type="button" onClick={() => setCurrencyId('')}>{__tr("إلغاء")}</button></form>)}
    <DataTable headings={["الرمز", "الحساب", "النوع", "العملة", "رصيد الدفتر", "رصيد المجموعة", "فرق الرصيد المحفوظ", "الإجراءات"]} rows={accounts.map(a => [a.code, __localizedField(a, 'name_ar'), typeNames[a.account_type], a.currency || "لم تُحدد", money(a.ledger_balance), money(a.group_balance), <span className={Number(a.difference) ? 'text-red-700 font-bold' : ''}>{__tr(money(a.difference))}</span>, <div className="flex gap-3 whitespace-nowrap"><button className="text-purple-700" onClick={async () => setStatement(await inspect('statement', a.id))}>{__tr("كشف حساب")}</button>{__tr(!a.currency && <button className="text-purple-700" onClick={() => setCurrencyId(a.id)}>{__tr("تحديد العملة")}</button>)}</div>])} />
    {__tr(statement && <div className="border rounded p-4 space-y-3"><h3 className="font-bold">{__tr("كشف حساب: ")}{__tr(__localizedField(statement.account, "name_ar"))} — {__tr(statement.account.currency || "عملة قديمة غير محددة")}</h3><p className="text-sm">{__tr("الرصيد الجاري هنا مدين موجب أو دائن سالب.")}</p><DataTable headings={["التاريخ", "القيد", "الوصف", "مدين", "دائن", "الرصيد الجاري"]} rows={statement.lines.map(l => [l.entry_date, l.entry_number, l.description, money(l.debit), money(l.credit), money(l.running_debit_balance)])} /><button onClick={() => setStatement(null)}>{__tr("إغلاق")}</button></div>)}
  </>;
}
function InvoicesPanel({
  invoices,
  lookups,
  submit,
  inspect,
  isAdmin,
  onReverse
}) {
  __useLocale();
  const [form, setForm] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    setDetail(null);
    setReceipt(null);
  }, [invoices]);
  const change = (key, value) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const lineChange = (index, key, value) => setForm(prev => ({
    ...prev,
    items: prev.items.map((l, i) => i === index ? {
      ...l,
      [key]: value
    } : l)
  }));
  return <>
    <p className="text-sm text-gray-600">{__tr("المسودة لا تؤثر على الأرصدة. الاعتماد يسجل مديونية العميل والإيراد والضريبة، والتحصيل يسجل سند قبض ويخفض المتبقي. «معتمدة» لا تعني إرسال بريد.")}</p>
    <button className={buttonClass} onClick={() => setForm({
      user_id: '',
      booking_id: '',
      currency: '',
      receivable_account_id: '',
      revenue_account_id: '',
      tax_account_id: '',
      tax_rate: '0',
      discount: '0',
      due_date: '',
      notes: '',
      items: [{
        description: '',
        quantity: 1,
        unit_price: ''
      }]
    })}>{__tr("فاتورة جديدة")}</button>
    {__tr(form && <form className="border rounded p-4 space-y-4" onSubmit={async e => {
      e.preventDefault();
      if (await submit('invoice-create', form)) setForm(null);
    }}>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label={__tr("العميل")}><select required className={fieldClass} value={form.user_id} onChange={e => setForm({
            ...form,
            user_id: e.target.value,
            booking_id: ''
          })}><option value="">{__tr("اختر العميل")}</option>{__tr(lookups.customers.map(c => <option key={c.id} value={c.id}>{__tr(c.full_name)}</option>))}</select></Field>
        <Currency value={form.currency} onChange={currency => setForm({
          ...form,
          currency,
          booking_id: '',
          receivable_account_id: '',
          revenue_account_id: '',
          tax_account_id: ''
        })} />
        <Field label={__tr("الحجز (اختياري)")}><select className={fieldClass} value={form.booking_id} onChange={e => change('booking_id', e.target.value)}><option value="">{__tr("دون حجز")}</option>{__tr(lookups.bookings.filter(b => String(b.user_id) === String(form.user_id) && b.currency === form.currency).map(b => <option key={b.id} value={b.id}>{__tr(b.booking_type)} — {__tr(b.id)}</option>))}</select></Field>
        <AccountSelect label={__tr("حساب ذمم العميل")} accounts={lookups.accounts} currency={form.currency} type="asset" value={form.receivable_account_id} onChange={v => change('receivable_account_id', v)} />
        <AccountSelect label={__tr("حساب الإيراد")} accounts={lookups.accounts} currency={form.currency} type="revenue" value={form.revenue_account_id} onChange={v => change('revenue_account_id', v)} />
        <Field label={__tr("الاستحقاق")}><input type="date" className={fieldClass} value={form.due_date} onChange={e => change('due_date', e.target.value)} /></Field>
      </div>
      {__tr(form.items.map((item, index) => <div className="grid md:grid-cols-4 gap-3" key={index}>
        <Field label={__tr("" + __tr("وصف البند ") + (index + 1))}><input required className={fieldClass} value={item.description} onChange={e => lineChange(index, 'description', e.target.value)} /></Field>
        <Field label={__tr("الكمية")}><input required type="number" min="1" max="10000" step="1" className={fieldClass} value={item.quantity} onChange={e => lineChange(index, 'quantity', e.target.value)} /></Field>
        <Field label={__tr("سعر الوحدة")}><input required type="number" min="0.01" step="0.01" className={fieldClass} value={item.unit_price} onChange={e => lineChange(index, 'unit_price', e.target.value)} /></Field>
        <button type="button" disabled={form.items.length === 1} onClick={() => change('items', form.items.filter((_, i) => i !== index))}>{__tr("حذف البند")}</button>
      </div>))}
      <button type="button" className="text-purple-700" onClick={() => change('items', [...form.items, {
        description: '',
        quantity: 1,
        unit_price: ''
      }])}>{__tr("إضافة بند")}</button>
      <div className="grid md:grid-cols-3 gap-4"><Field label={__tr("خصم من المجموع قبل الضريبة")}><input type="number" required min="0" step="0.01" className={fieldClass} value={form.discount} onChange={e => change('discount', e.target.value)} /></Field><Field label={__tr("نسبة الضريبة %")}><input type="number" required min="0" max="100" step="0.01" className={fieldClass} value={form.tax_rate} onChange={e => change('tax_rate', e.target.value)} /></Field>{__tr(Number(form.tax_rate) > 0 && <AccountSelect label={__tr("حساب الضريبة المستحقة")} accounts={lookups.accounts} currency={form.currency} type="liability" value={form.tax_account_id} onChange={v => change('tax_account_id', v)} />)}</div>
      <p className="text-sm text-gray-500">{__tr("يحسب الخادم الإجمالي والضريبة بعد الخصم. راجع تفاصيل المسودة قبل اعتمادها.")}</p>
      <button className={buttonClass}>{__tr("حفظ مسودة")}</button> <button type="button" onClick={() => setForm(null)}>{__tr("إلغاء")}</button>
    </form>)}
    {__tr(receipt && <form className="border rounded p-4 space-y-4 bg-purple-50" onSubmit={async e => {
      e.preventDefault();
      if (!window.confirm("" + __tr("تسجيل تحصيل ") + receipt.amount + " " + receipt.currency + __tr(" على الفاتورة؟"))) return;
      if (await submit('invoice-receive', {
        id: receipt.id,
        amount: receipt.amount,
        cash_account_id: receipt.cash_account_id,
        payment_method: receipt.payment_method,
        date: receipt.date
      })) {
        setReceipt(null);
        setDetail(null);
      }
    }}>
      <h3 className="font-bold">{__tr("تحصيل الفاتورة ")}{__tr(receipt.invoice_number)}{__tr(" — المتبقي ")}{__tr(money(receipt.remaining))} {__tr(receipt.currency)}</h3>
      <div className="grid md:grid-cols-4 gap-3"><Field label={__tr("المبلغ")}><input required type="number" min="0.01" max={receipt.remaining} step="0.01" className={fieldClass} value={receipt.amount} onChange={e => setReceipt({
            ...receipt,
            amount: e.target.value
          })} /></Field><AccountSelect label={__tr("الصندوق أو البنك")} accounts={lookups.accounts} currency={receipt.currency} type="asset" exclude={receipt.receivable_account_id} value={receipt.cash_account_id} onChange={v => setReceipt({
          ...receipt,
          cash_account_id: v
        })} /><Field label={__tr("طريقة التحصيل")}><select className={fieldClass} value={receipt.payment_method} onChange={e => setReceipt({
            ...receipt,
            payment_method: e.target.value
          })}><option value="cash">{__tr("نقدًا")}</option><option value="bank_transfer">{__tr("تحويل بنكي")}</option></select></Field><Field label={__tr("تاريخ التحصيل")}><input required type="date" className={fieldClass} value={receipt.date} onChange={e => setReceipt({
            ...receipt,
            date: e.target.value
          })} /></Field></div><button className={buttonClass}>{__tr("تأكيد التحصيل والترحيل")}</button> <button type="button" onClick={() => setReceipt(null)}>{__tr("إلغاء")}</button>
    </form>)}
    <DataTable headings={["الفاتورة", "العميل", "العملة", "الإجمالي", "المحصل", "المتبقي", "الحالة", "الإجراءات"]} rows={invoices.map(i => [i.invoice_number, i.customer_name, i.currency, money(i.total), money(i.paid_amount), money(i.remaining), i.managed ? statusNames[i.status] || i.status : "فاتورة قديمة غير مربوطة", <div className="flex flex-wrap gap-3"><button className="text-purple-700" onClick={async () => setDetail(await inspect('invoices', i.id))}>{__tr("تفاصيل")}</button>{__tr(isAdmin && i.managed && i.status === 'draft' && <><button className="text-green-700" onClick={async () => {
          if (window.confirm("" + __tr("اعتماد الفاتورة بمبلغ ") + i.total + " " + i.currency + __tr(" وترحيل القيد؟"))) {
            await submit('invoice-post', {
              id: i.id
            });
            setDetail(null);
          }
        }}>{__tr("اعتماد")}</button><button className="text-red-700" onClick={async () => {
          if (window.confirm(__tr("إلغاء المسودة؟"))) await submit('invoice-cancel', {
            id: i.id
          });
        }}>{__tr("إلغاء المسودة")}</button></>)}{__tr(isAdmin && i.journal_entry_id && i.status !== 'cancelled' && Number(i.paid_amount) === 0 && <button className="text-red-700" onClick={() => onReverse({
        action: 'invoice-reverse',
        id: i.id,
        label: "" + __tr("إلغاء الفاتورة ") + i.invoice_number
      })}>{__tr("إلغاء بقيد عكسي")}</button>)}{__tr(isAdmin && i.journal_entry_id && Number(i.remaining) > 0 && ['sent', 'overdue'].includes(i.status) && <button className="text-green-700" onClick={() => setReceipt({
        ...i,
        amount: i.remaining,
        cash_account_id: '',
        payment_method: 'cash',
        date: today()
      })}>{__tr("تحصيل")}</button>)}</div>])} />
    {__tr(detail && <div className="border rounded p-4 space-y-4"><h3 className="font-bold">{__tr("تفاصيل ")}{__tr(detail.invoice_number)}</h3><DataTable headings={["البند", "الكمية", "السعر", "الإجمالي"]} rows={(detail.items || []).map(i => [i.description, i.quantity, money(i.unit_price), money(i.total)])} /><p>{__tr("المجموع: ")}{__tr(money(detail.subtotal))}{__tr(" — الخصم: ")}{__tr(money(detail.discount))}{__tr(" — الضريبة: ")}{__tr(money(detail.tax_amount))}{__tr(" — الإجمالي: ")}{__tr(money(detail.total))} {__tr(detail.currency)}</p><p className="break-all">{__tr("قيد الاعتماد: ")}{__tr(detail.journal_entry_id || "لم يتم الاعتماد")}</p><DataTable headings={["سند القبض", "المبلغ", "العملة", "معرف الدفعة", "القيد", "الحالة"]} rows={(detail.payments || []).map(p => [p.voucher_number, money(p.amount), p.currency, p.transaction_id, p.journal_entry_id, p.reversal_entry_id ? <span>{__tr("معكوس: ")}{__tr(p.reversal_reason)}<br />{__tr(p.reversal_entry_id)}</span> : isAdmin && <button className="text-red-700" onClick={() => onReverse({
        action: 'receipt-reverse',
        id: p.id,
        label: "" + __tr("عكس تحصيل ") + p.amount + " " + p.currency + " \u2014 " + p.voucher_number
      })}>{__tr("عكس التحصيل")}</button>])} /><button onClick={() => setDetail(null)}>{__tr("إغلاق")}</button></div>)}
  </>;
}
function JournalPanel({
  entries,
  accounts,
  submit,
  onReverse
}) {
  __useLocale();
  const [form, setForm] = useState(null);
  const [expanded, setExpanded] = useState('');
  const changeLine = (index, key, value) => setForm(prev => ({
    ...prev,
    lines: prev.lines.map((line, i) => i === index ? {
      ...line,
      [key]: value
    } : line)
  }));
  return <>
    <button className={buttonClass} onClick={() => setForm({
      description: '',
      date: today(),
      currency: '',
      lines: [{
        account_id: '',
        debit: '',
        credit: ''
      }, {
        account_id: '',
        debit: '',
        credit: ''
      }]
    })}>{__tr("قيد يدوي / رصيد افتتاحي")}</button>
    {__tr(form && <form className="border rounded p-4 space-y-4" onSubmit={async e => {
      e.preventDefault();
      const payload = {
        ...form,
        lines: form.lines.map(l => ({
          ...l,
          debit: l.debit || '0',
          credit: l.credit || '0'
        }))
      };
      if (await submit('journal-create', payload)) setForm(null);
    }}>
      <div className="grid md:grid-cols-3 gap-3"><Field label={__tr("الوصف")}><input required className={fieldClass} value={form.description} onChange={e => setForm({
            ...form,
            description: e.target.value
          })} /></Field><Field label={__tr("التاريخ")}><input required type="date" className={fieldClass} value={form.date} onChange={e => setForm({
            ...form,
            date: e.target.value
          })} /></Field><Currency value={form.currency} onChange={currency => setForm({
          ...form,
          currency,
          lines: form.lines.map(l => ({
            ...l,
            account_id: ''
          }))
        })} /></div>
      {__tr(form.lines.map((l, index) => <div key={index} className="grid md:grid-cols-4 gap-3"><AccountSelect label={__tr("" + __tr("الحساب ") + (index + 1))} accounts={accounts} currency={form.currency} value={l.account_id} onChange={v => changeLine(index, 'account_id', v)} /><Field label={__tr("مدين")}><input type="number" min="0" step="0.01" className={fieldClass} value={l.debit} onChange={e => changeLine(index, 'debit', e.target.value)} /></Field><Field label={__tr("دائن")}><input type="number" min="0" step="0.01" className={fieldClass} value={l.credit} onChange={e => changeLine(index, 'credit', e.target.value)} /></Field><button type="button" disabled={form.lines.length <= 2} onClick={() => setForm({
          ...form,
          lines: form.lines.filter((_, i) => i !== index)
        })}>{__tr("حذف السطر")}</button></div>))}
      <button type="button" className="text-purple-700" onClick={() => setForm({
        ...form,
        lines: [...form.lines, {
          account_id: '',
          debit: '',
          credit: ''
        }]
      })}>{__tr("إضافة سطر")}</button><p className="text-sm">{__tr("يجب أن يتساوى المدين والدائن. حفظ المسودة لا يغيّر الرصيد حتى ترحيلها.")}</p><button className={buttonClass}>{__tr("حفظ مسودة القيد")}</button> <button type="button" onClick={() => setForm(null)}>{__tr("إلغاء")}</button>
    </form>)}
    <DataTable headings={["القيد", "التاريخ", "الوصف", "العملة", "المدين", "الدائن", "الحالة", "الإجراءات"]} rows={entries.map(j => [j.entry_number, j.entry_date, j.description, j.currency || "غير محددة", money(j.total_debit), money(j.total_credit), j.reversal_entry_id ? "معكوس" : Number(j.is_posted) ? "مرحّل" : "مسودة", <div className="flex gap-3"><button onClick={() => setExpanded(expanded === j.id ? '' : j.id)}>{__tr("التفاصيل")}</button>{__tr(Number(j.is_posted) > 0 && !j.reversal_entry_id && j.source_key?.startsWith('manual:') && <button className="text-red-700" onClick={() => onReverse({
        action: 'journal-reverse',
        id: j.id,
        label: "" + __tr("عكس القيد ") + j.entry_number
      })}>{__tr("عكس القيد")}</button>)}{__tr(!Number(j.is_posted) && j.source_key?.startsWith('manual:') && <button className="text-green-700" onClick={async () => {
        if (window.confirm(__tr("ترحيل القيد وتحديث الأرصدة؟"))) await submit('journal-post', {
          id: j.id
        });
      }}>{__tr("ترحيل")}</button>)}</div>])} />
    {__tr(expanded && <DataTable headings={["الحساب", "الاسم", "مدين", "دائن"]} rows={(entries.find(j => j.id === expanded)?.lines || []).map(l => [l.account_code, l.account_name, money(l.debit), money(l.credit)])} />)}
  </>;
}
function TrialPanel({
  data
}) {
  __useLocale();
  return <div className="space-y-6"><p className="text-sm text-gray-600">{__tr("يعتمد على القيود المرحلة فقط ويفصل كل عملة. أرصدة المجموعات لا تُجمع مرة إضافية.")}</p>{__tr(data.unassigned?.length > 0 && <p role="alert" className="bg-amber-50 p-3">{__tr("يوجد ")}{__tr(data.unassigned.length)}{__tr(" حسابًا بعملة غير محددة؛ لا يشملها هذا الميزان. حدد عملة الحسابات الخالية، وراجع الأرصدة التاريخية قبل نقلها.")}</p>)}{__tr(data.currencies.map(g => <div key={g.currency} className="border rounded p-4"><h3 className="font-bold text-lg">{__tr(g.currency)} — {__tr(g.balanced ? "متوازن" : "غير متوازن")}</h3>{__tr(g.differences > 0 && <p role="alert" className="text-red-700">{__tr("يوجد اختلاف بين الرصيد المحفوظ والقيود في ")}{__tr(g.differences)}{__tr(" حساب.")}</p>)}<DataTable headings={["الرمز", "الحساب", "مدين", "دائن"]} rows={g.accounts.map(a => [a.code, __localizedField(a, 'name_ar'), money(a.debit_balance), money(a.credit_balance)])} /><p className="font-bold mt-4">{__tr("الإجمالي المدين: ")}{__tr(money(g.debit))}{__tr(" — الدائن: ")}{__tr(money(g.credit))}</p></div>))}</div>;
}
